import React, { useEffect, useState } from "react";
import classnames from "classnames";

import { getProvider } from "../shared/data";
import { updateFn } from "../shared/events";

const checkValue = (defaultValue, value) => {
    const useValue = typeof defaultValue === "boolean" || defaultValue ? defaultValue : value;

    return typeof useValue === "boolean" || useValue ? useValue : "";
};

const Option = ({ value, text, hidden }) => {
    return (
        <option
            value={value}
            label={text}
            hidden={hidden}
        >
            {text}
        </option>
    );
};

const Options = ({ name, options }) => {
    return options.map((opt, index) => {
        return (
            <Option
                key={`select${name}${opt.value}_${index}`}
                value={opt.value}
                text={opt.displayText || opt.text}
                hidden={opt.hidden}
            />
        );
    });
};

const OptionGroup = (props) => {
    return (
        <optgroup label={props.label}>
            <Options
                name={props.name}
                options={props.options}
            />
        </optgroup>
    );
};

export const Select = React.forwardRef((props, ref) => {
    const [value, setValue] = useState(checkValue(props.defaultValue, props.value));
    const [options, setOptions] = useState(props.options || []);

    useEffect(() => {
        const useValue = checkValue(props.defaultValue, props.value);
        setValue(useValue);
    }, [props.defaultValue, props.value]);

    useEffect(() => {
        loadOptions();
    }, [props.options]);

    const onChange = (e) => {
        setValue(e.currentTarget.value);
    };

    const loadOptions = async () => {
        const newOptions = props.provider ? await getProvider(props.provider) : props.loadOptions ? await props.loadOptions() : props.options || [];

        // Catch for success or failure
        if (newOptions.success || newOptions.error) {
            return;
        }

        if (!props.stopAutoSelect && newOptions.length === 1 && props.updateOnChange) {
            if (newOptions[0].value !== value) {
                props.updateOnChange(props.name, newOptions[0].value);
            }
        }

        setOptions(newOptions);
    };

    const fullOptions = props.excludeSelect ? options : [{ text: "-- Select --", value: "" }, ...options];
    const groupOptions = props.groups;

    const optionItems =
        fullOptions.length > 0 ? (
            <Options
                name={props.name}
                options={fullOptions}
            />
        ) : null;
    const groups = groupOptions ? (
        groupOptions.length > 0 ? (
            groupOptions.map((group, index) => {
                return group.options ? (
                    <OptionGroup
                        key={`selectGroup_${index}`}
                        name={props.name}
                        options={group.options}
                        label={group.label}
                    />
                ) : (
                    <Option
                        key={`select${props.name}${group.value}_${index}`}
                        value={group.value}
                        text={group.label}
                    />
                );
            })
        ) : (
            <Options
                name={props.name}
                options={groupOptions}
            />
        )
    ) : null;

    return (
        <select
            ref={ref}
            id={props.name}
            name={props.name}
            disabled={props.disabled}
            hidden={props.invisible}
            multiple={props.multiple}
            required={props.required}
            size={props.size}
            value={value}
            onChange={props.onChange || updateFn(props.updateOnChange) || onChange}
            onBlur={props.onBlur || updateFn(props.updateOnBlur)}
            className={classnames({ "-error": props.invalid }, props.className)}
            onFocus={props.onFocus}
        >
            {optionItems}
            {groups}
        </select>
    );
});

Select.defaultProps = {
    defaultValue: "",
};
